<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { usePipelinePermissions } from 'dashboard/composables/pipelines/usePipelinePermissions';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import RoleModal from './RoleModal.vue';

const store = useStore();
useI18n();
const showAlert = useAlert;
const { canCreateCustomRoles, isPro } = usePipelinePermissions();

const accountId = computed(() => store.getters.getCurrentAccountId);
const roles = computed(() => store.getters['pipelineRoles/getPipelineRoles']);
const uiFlags = computed(() => store.getters['pipelineRoles/getUIFlags']);

const showModal = ref(false);
const modalMode = ref('create');
const selectedRole = ref(null);
const showDeleteConfirm = ref(false);
const roleToDelete = ref(null);

onMounted(async () => {
  await Promise.all([
    store.dispatch('pipelineRoles/get', { accountId: accountId.value }),
    store.dispatch('pipelineRoles/getPermissionKeys', {
      accountId: accountId.value,
    }),
  ]);
});

const KEY_LABELS = {
  'conversations.view_all': 'Ver todas as conversas',
  'conversations.view_unassigned': 'Ver não atribuídas',
  'conversations.assign': 'Atribuir conversas',
  'conversations.bulk_actions': 'Ações em lote',
  'pipeline.view': 'Ver Pipeline',
  'pipeline.manage': 'Gerenciar Pipeline',
  'deals.create': 'Criar Deals',
  'deals.edit': 'Editar Deals',
  'deals.delete': 'Excluir Deals',
  'deals.view_all': 'Ver todos os Deals',
  'contacts.view': 'Ver Contatos',
  'contacts.create': 'Criar Contatos',
  'contacts.edit': 'Editar Contatos',
  'contacts.delete': 'Excluir Contatos',
  'contacts.import': 'Importar Contatos',
  'calendar.view': 'Ver Calendário',
  'calendar.manage': 'Gerenciar Calendário',
  'reports.view': 'Ver Relatórios',
  'knowledge_base.view': 'Ver Base de Conhecimento',
  'knowledge_base.manage': 'Gerenciar Base de Conhecimento',
  'ai_agents.view': 'Ver Agentes IA',
  'ai_agents.manage': 'Gerenciar Agentes IA',
  'settings.agents': 'Config: Agentes',
  'settings.inboxes': 'Config: Caixas',
  'settings.teams': 'Config: Equipes',
  'settings.labels': 'Config: Labels',
  'settings.roles': 'Config: Funções',
  'settings.subscription': 'Ver Assinatura',
};

const formatPermissions = role => {
  if (role.admin_role || (role.system_role && !role.default_agent_role)) {
    return 'Acesso total ao sistema';
  }
  const keys = role.permission_keys || [];
  if (keys.includes('*')) return 'Acesso total ao sistema';
  if (!keys.length) return '—';
  const labels = keys.slice(0, 3).map(k => KEY_LABELS[k] || k);
  const extra = keys.length > 3 ? ` +${keys.length - 3}` : '';
  return labels.join(', ') + extra;
};

function openCreateModal() {
  selectedRole.value = null;
  modalMode.value = 'create';
  showModal.value = true;
}

function openEditModal(role) {
  selectedRole.value = role;
  modalMode.value = 'edit';
  showModal.value = true;
}

function openDeleteConfirm(role) {
  roleToDelete.value = role;
  showDeleteConfirm.value = true;
}

async function handleSave(payload) {
  try {
    if (modalMode.value === 'create') {
      await store.dispatch('pipelineRoles/create', {
        accountId: accountId.value,
        role: payload,
      });
      showAlert('Função criada com sucesso.');
    } else {
      await store.dispatch('pipelineRoles/update', {
        accountId: accountId.value,
        id: selectedRole.value.id,
        role: payload,
      });
      showAlert('Função atualizada com sucesso.');
    }
    showModal.value = false;
  } catch (e) {
    showAlert(e?.response?.data?.error || 'Erro ao salvar função.');
  }
}

async function confirmDelete() {
  try {
    await store.dispatch('pipelineRoles/delete', {
      accountId: accountId.value,
      id: roleToDelete.value.id,
    });
    showAlert('Função excluída.');
  } catch (e) {
    showAlert(e?.response?.data?.error || 'Erro ao excluir função.');
  } finally {
    showDeleteConfirm.value = false;
    roleToDelete.value = null;
  }
}

const canEdit = role => !role.system_role || role.default_agent_role;
const canDelete = role => !role.system_role && isPro.value;
const isAdminRole = role => role.system_role && !role.default_agent_role;
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('PIPELINE_PERMISSIONS.TITLE')"
    :no-records-found="!roles.length && !uiFlags.isFetching"
    :no-records-message="$t('PIPELINE_PERMISSIONS.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PIPELINE_PERMISSIONS.TITLE')"
        :description="$t('PIPELINE_PERMISSIONS.DESCRIPTION')"
        feature-name="pipeline_permissions"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('PIPELINE_PERMISSIONS.NEW_ROLE')"
            :disabled="!canCreateCustomRoles"
            @click="openCreateModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="header in [
              $t('PIPELINE_PERMISSIONS.TABLE.NAME'),
              $t('PIPELINE_PERMISSIONS.TABLE.DESCRIPTION'),
              $t('PIPELINE_PERMISSIONS.TABLE.PERMISSIONS'),
              $t('PIPELINE_PERMISSIONS.TABLE.ACTIONS'),
            ]"
            :key="header"
            class="py-4 ltr:pr-4 rtl:pl-4 font-semibold text-left text-n-slate-11"
          >
            {{ header }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr v-for="role in roles" :key="role.id">
            <td class="max-w-xs py-4 ltr:pr-4 rtl:pl-4 align-baseline">
              <div class="flex items-center gap-2">
                <span
                  class="i-lucide-shield text-base"
                  :class="
                    isAdminRole(role) ? 'text-woot-500' : 'text-slate-400'
                  "
                />
                <span class="font-medium truncate" :title="role.name">{{
                  role.name
                }}</span>
                <span
                  v-if="role.system_role"
                  class="text-xs px-1.5 py-0.5 bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400 rounded"
                >
                  {{ $t('PIPELINE_PERMISSIONS.SYSTEM_BADGE') }}
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 whitespace-normal align-baseline">
              {{ role.description || '—' }}
            </td>
            <td
              class="py-4 ltr:pr-4 rtl:pl-4 whitespace-normal align-baseline md:break-words max-w-sm text-sm"
            >
              {{ formatPermissions(role) }}
            </td>
            <td class="flex justify-end gap-1 py-4">
              <Button
                v-if="canEdit(role)"
                v-tooltip.top="$t('PIPELINE_PERMISSIONS.EDIT_ROLE')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                @click="openEditModal(role)"
              />
              <Button
                v-if="canDelete(role)"
                v-tooltip.top="$t('PIPELINE_PERMISSIONS.DELETE_ROLE')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                @click="openDeleteConfirm(role)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Hint para planos não-Pro -->
      <div
        v-if="!canCreateCustomRoles"
        class="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 w-fit"
      >
        <span class="i-lucide-lock text-slate-400 text-sm" />
        <span class="text-xs text-slate-500 dark:text-slate-400">
          {{ $t('PIPELINE_PERMISSIONS.UPGRADE_HINT') }}
        </span>
      </div>
    </template>
  </SettingsLayout>

  <!-- Modal de criar/editar -->
  <woot-modal v-model:show="showModal" :on-close="() => (showModal = false)">
    <RoleModal
      v-if="showModal"
      :mode="modalMode"
      :role="selectedRole"
      @save="handleSave"
      @close="showModal = false"
    />
  </woot-modal>

  <!-- Modal de confirmação de exclusão -->
  <woot-delete-modal
    v-model:show="showDeleteConfirm"
    :on-close="() => (showDeleteConfirm = false)"
    :on-confirm="confirmDelete"
    :title="$t('PIPELINE_PERMISSIONS.DELETE_ROLE_TITLE')"
    :message="$t('PIPELINE_PERMISSIONS.DELETE_ROLE_MESSAGE')"
    :message-value="roleToDelete?.name"
    :confirm-text="$t('PIPELINE_PERMISSIONS.DELETE_CONFIRM_YES')"
    :reject-text="$t('PIPELINE_PERMISSIONS.DELETE_CONFIRM_NO')"
  />
</template>
